import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useMerchantId } from '@/hooks/useMerchantId';
import { formatUZS } from '@/lib/format';
import { dateLocale } from '@/lib/locale';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Clock, DollarSign, Search, Filter, AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import ConsistencyBadge from '@/components/murabaha/ConsistencyBadge';
import { checkMurabahaConsistency } from '@/lib/murabaha-consistency';
import { BranchFilter } from '@/components/BranchFilter';
import { useTranslation } from 'react-i18next';
import { InvoiceBadge } from '@/components/invoices/InvoiceBadge';
import { InvoiceProblemBadge, PayoutBadge } from '@/components/payouts/PayoutBadge';
import { INVOICE_FILTER_STATUSES } from '@/lib/invoices';
import { FETCH_ALL_MAX_PAGES, FETCH_ALL_PER_PAGE } from '@/lib/fetch-all';


const statusColors: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground',
  PendingCustomerConfirmation: 'bg-warning/10 text-warning',
  Approved: 'bg-info/10 text-info',
  Rejected: 'bg-destructive/10 text-destructive',
  Completed: 'bg-success/10 text-success',
  Cancelled: 'bg-muted text-muted-foreground',
};

const paymentStatusColors: Record<string, string> = {
  none: 'bg-muted text-muted-foreground',
  in_progress: 'bg-warning/10 text-warning',
  cancelled: 'bg-destructive/10 text-destructive',
  paid: 'bg-success/10 text-success',
};

type MurabahaCase = components['schemas']['MurabahaCaseResource'];


const statusFilterKeys = ['Draft', 'PendingCustomerConfirmation', 'Approved', 'Completed', 'Rejected', 'Cancelled'];
const paymentFilterKeys = ['none', 'in_progress', 'paid', 'cancelled'];

export default function MurabahaQueue() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { merchantId } = useMerchantId();
  const [search, setSearch] = useState('');
  const [combinedFilter, setCombinedFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');

  const { data: murabahaCases = [], isLoading } = useQuery({
    queryKey: ['/murabaha/cases', merchantId],
    queryFn: async () => {
      const all: MurabahaCase[] = [];
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const { data, error } = await apiClient.GET('/murabaha/cases', { params: { query: { page, per_page: FETCH_ALL_PER_PAGE } as never } });
        if (error) throw error;
        all.push(...(data?.data ?? []));
        if (page >= (data?.meta?.last_page ?? 1)) break;
      }
      return all;
    },
    enabled: !!merchantId,
  });

  // FR-99: as Lovable's useInvoices({ sync: true }) — pull fresh invoice statuses when the queue opens.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!merchantId) return;
    apiClient.POST('/invoices/sync').then(({ data }) => {
      if ((data?.processed ?? 0) + (data?.refreshed ?? 0) > 0) {
        queryClient.invalidateQueries({ queryKey: ['/murabaha/cases'] });
      }
    }).catch(() => undefined);
  }, [merchantId, queryClient]);

  // Buyurtma (order) — tovar narxini (ustamasiz) saqlaydi, do'konga shu summa to'lanadi.
  // Shartnoma (contract) — Loan Managerdan kelgan ID/holat. Ikkalasi ham
  // endi /murabaha/cases javobiga embed qilingan, alohida so'rov kerak emas.
  const payoutByCase = new Map<number, number>(
    murabahaCases.map(mc => [mc.id, Number((mc.order ?? [])[0]?.total_amount ?? mc.total_amount)])
  );
  // Lovable's unified ID: murabaha_cases.contract_id is the contract number; ours is a
  // pointer, so the API resolves it to `contract_number` (the case's own contract number).
  const contractIdOf = (mc: MurabahaCase) => mc.contract_number || mc.contract?.contract_number || null;
  const issuesOf = (mc: MurabahaCase) => checkMurabahaConsistency(
    { status: mc.status, payment_status: mc.payment_status, contract_id: mc.contract_number ?? null },
    mc.contract ? { status: mc.contract.status, contract_number: mc.contract.contract_number } : null
  ).issues;

  const statusFilter = combinedFilter.startsWith('s:') ? combinedFilter.slice(2) : 'all';
  const paymentFilter = combinedFilter.startsWith('p:') ? combinedFilter.slice(2) : 'all';
  const invoiceFilter = combinedFilter.startsWith('i:') ? combinedFilter.slice(2) : 'all';
  const onlyMismatch = combinedFilter === 'mismatch';

  const filtered = murabahaCases.filter(mc => {
    const customer = mc.customer;
    const matchSearch = !search ||
      customer?.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      String(mc.id).includes(search) ||
      (contractIdOf(mc) || '').includes(search);
    const matchStatus = statusFilter === 'all' || mc.status === statusFilter;
    const matchPayment = paymentFilter === 'all' || mc.payment_status === paymentFilter;
    const matchMismatch = !onlyMismatch || issuesOf(mc).length > 0;
    const matchBranch = branchFilter === 'all' || String(mc.branch_id) === branchFilter;
    const matchInvoice = invoiceFilter === 'all' || mc.invoice?.status === invoiceFilter;
    return matchSearch && matchStatus && matchPayment && matchMismatch && matchBranch && matchInvoice;
  });

  const mismatchCount = murabahaCases.filter(mc => issuesOf(mc).length > 0).length;

  const pendingCases = murabahaCases.filter(m => m.status === 'PendingCustomerConfirmation' || m.status === 'Draft');
  const pendingCount = pendingCases.length;
  const pendingSum = pendingCases.reduce((s, m) => s + Number(m.total_amount), 0);


  return (
    <div className="w-full min-w-0 px-2 py-4 sm:px-3 md:px-0 md:py-6 animate-fade-in">
      <h1 className="text-xl font-semibold text-foreground mb-4">{t('mur_queue.title')}</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">{t('mur_queue.pending_contracts')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : t('mur_queue.count', { n: pendingCount })}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">{t('mur_queue.pending_sum')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : formatUZS(pendingSum)}</p>
        </div>
        <button
          type="button"
          onClick={() => setCombinedFilter(onlyMismatch ? 'all' : 'mismatch')}
          className={`text-left bg-card rounded-xl border p-4 transition-colors hover:bg-muted/30 ${
            mismatchCount > 0 ? 'border-destructive/40' : 'border-border'
          } ${onlyMismatch ? 'ring-2 ring-destructive/40' : ''}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className={mismatchCount > 0 ? 'text-destructive' : 'text-muted-foreground'} />
            <span className="text-xs text-muted-foreground">{t('mur_queue.mismatch')}</span>
          </div>
          <p className={`text-lg font-semibold ${mismatchCount > 0 ? 'text-destructive' : 'text-foreground'}`}>
            {isLoading ? '...' : t('mur_queue.count', { n: mismatchCount })}
          </p>
        </button>
      </div>


      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={t('mur_queue.search')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <BranchFilter value={branchFilter} onChange={setBranchFilter} />
        <Select value={combinedFilter} onValueChange={setCombinedFilter}>
          <SelectTrigger className="w-[170px]">
            <Filter size={14} className="text-muted-foreground mr-1" />
            <SelectValue placeholder={t('mur_queue.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mur_queue.all')}</SelectItem>
            <SelectItem value="mismatch">{t('mur_queue.only_mismatch')}</SelectItem>
            <SelectSeparator />

            <SelectGroup>
              <SelectLabel>{t('mur_queue.group_status')}</SelectLabel>
              {statusFilterKeys.map(k => (
                <SelectItem key={k} value={`s:${k}`}>{t(`mur_status.${k}`)}</SelectItem>
              ))}
            </SelectGroup>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>{t('mur_queue.group_payment')}</SelectLabel>
              {paymentFilterKeys.map(k => (
                <SelectItem key={k} value={`p:${k}`}>{k === 'none' ? t('mur_queue.pay_filter_none') : t(`mur_queue.pay.${k}`)}</SelectItem>
              ))}
            </SelectGroup>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>{t('invoice.column')}</SelectLabel>
              {INVOICE_FILTER_STATUSES.map(k => (
                <SelectItem key={k} value={`i:${k}`}>{t(`invoice.status.${k}`)}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_contract_id')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_customer')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_branch')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_contract_sum')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_payout')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_status')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('invoice.column')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_payment')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mur_queue.col_date')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">{t('mur_queue.loading')}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">{t('mur_queue.empty')}</td></tr>
            ) : (
              filtered.map(mc => {
                const customer = mc.customer;
                const contractTotal = Number(mc.total_amount);
                const payout = payoutByCase.get(mc.id) ?? contractTotal;
                const markup = Math.max(0, contractTotal - payout);
                const issues = issuesOf(mc);
                return (
                  <tr key={mc.id} className={`border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer ${issues.length > 0 ? 'bg-destructive/5' : ''}`} onClick={() => navigate(`/murabaha/${mc.id}`)}>

                    <td className="p-3 text-xs font-mono">
                      {contractIdOf(mc)
                        ? <span className="text-foreground">{contractIdOf(mc)}</span>
                        : <span className="text-muted-foreground font-sans">{mc.status === 'Approved' || mc.status === 'Completed' ? t('mur_queue.id_unassigned') : t('mur_queue.waiting')}</span>}
                    </td>
                    <td className="p-3 text-sm text-foreground">{customer?.full_name || '—'}</td>
                    <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">{mc.branch?.name || '—'}</td>
                    <td className="p-3 text-sm text-foreground">
                      {formatUZS(contractTotal)}
                      {markup > 0 && <span className="block text-[10px] text-muted-foreground">{t('mur_queue.markup')} {formatUZS(markup)}</span>}
                    </td>
                    <td className="p-3 text-sm font-medium text-success">
                      {formatUZS(payout)}
                      <span className="block text-[10px] text-muted-foreground font-normal">{t('mur_queue.goods_price')}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col items-start gap-1">
                        <Badge className={`text-xs border-0 ${statusColors[mc.status] || ''}`}>
                          {t(`mur_status.${mc.status}`, { defaultValue: mc.status })}
                        </Badge>
                        <ConsistencyBadge issues={issues} />
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col items-start gap-1">
                        <InvoiceBadge status={mc.invoice?.status} />
                        <PayoutBadge status={mc.payout?.status} />
                        <InvoiceProblemBadge show={mc.invoice_problem} />
                      </div>
                    </td>
                    <td className="p-3">
                      {mc.payment_status !== 'none' ? (
                        <Badge className={`text-xs border-0 ${paymentStatusColors[mc.payment_status] || ''}`}>
                          {t(`mur_queue.pay.${mc.payment_status}`, { defaultValue: mc.payment_status })}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(mc.created_at).toLocaleDateString(dateLocale())}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
